import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loader from '../../modules/layout/components/Loader';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import FiltersCard from '../../modules/layout/components/FiltersCard';
import Pagination from '../../modules/layout/components/Pagination';
import { useToast } from '../../modules/layout/components/ToastContext';
import { post, getAll } from '../_request';
import { Formik, Form, Field } from 'formik';
import SortDirection from '../../modules/layout/components/SortDirection';
import { IProfileFilterDto, IProfileItemDto } from '../../interfaces/IUtilities';
import  { useAuth, userHasRole } from '../../../setup/auth/authService'
import { AdminWeb, HelpDeskOperator } from '../../constants/constants';


export default function FindProfiles() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<IProfileItemDto[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState<'username' | 'fullname'>('username');
  const [asc, setAsc] = useState(true);

  const performSearch = async (body: IProfileFilterDto) => {
    if (loading) {
      return { success: false };
    }
    const hasAnyFilter = Boolean(
      body.Username || body.FirstName || body.LastName || body.Fullname || body.Email || body.MobileNumber || body.ProfileId || body.Category
    );

    if (!hasAnyFilter) {
      showToast && showToast(t('SelectAtLeastOneFilter'), 'warning');
      return { success: false };
    }

    setHasSearched(true);
    setLoading(true);
    setError(null);
    try {
      const result: any = await post('/api/UserEvents/findprofiles', body);
      const respStatus = result?.response?.status || result?.status;

      if (respStatus === 200 || respStatus === undefined) {
        const payload = result?.data?.Data ?? result?.data ?? result;
        const items = Array.isArray(payload?.Items)
          ? payload.Items
          : Array.isArray(payload)
          ? payload
          : [];
        setResults(items as IProfileItemDto[]);

        if ((!items || items.length === 0) && showToast) {
          showToast(t('NoDataFound'), 'info');
        }
        return { success: true };
      } else if (respStatus === 403) {
        navigate('/error/403');
      } else if (respStatus === 401) {
        navigate('/auth/login');
      } else if (respStatus === 500) {
        navigate('/error/500');
      } else {
        showToast && showToast(t('StatusCode', { code: String(respStatus) }), 'error');
      }
    } catch (e: any) {
      setError(String(e?.message ?? e));
      showToast && showToast(t('ConnectionError', { error: String(e) }), 'error');
    } finally {
      setLoading(false);
    }
    return { success: false };
  };

  const handleReset = () => {
    setResults([]);
    setError(null);
    setHasSearched(false);
  };

  const goToProfileEvents = (profile?: IProfileItemDto) => {
    if (!profile) return;
    const pid = profile.ProfileId;
    navigate(`/profiles/events/${encodeURIComponent(String(pid))}`, { state: { profile } });
  };


  const goToProfileDetails = (profile?: IProfileItemDto) => {
    if (!profile) return;

    const pid = profile.ProfileId ?? profile.UserId;
    navigate(`/profile-details/${encodeURIComponent(String(pid))}`);
  };


  useEffect(() => {
      fetchRole();
  }, []);

  const fetchRole = async () => { 
      const hasAdminWebRole = await userHasRole(AdminWeb);
      const hasHelpDeskOperatorRole = await userHasRole(HelpDeskOperator);
      if (!hasAdminWebRole || !hasHelpDeskOperatorRole)
          navigate("/error/403");
  };

 const dropdownOptions: Record<string, { value: string; label: string }[]> = {

      Category: [
        { value: 'Agent', label: t('Agent') },
        { value: 'Spoke', label: t('Spoke') },
        { value: 'Individual', label: t('Individuale') },
        { value: 'Organisation', label: t('Organisation') },
      ]
    };
  return (
    <div className="container mt-4">
      <AppBreadcrumb
        items={[{ title: t('Home'), href: '/' }, { title: t('FindProfiles') }]}
      />

      <h3 className="mb-3">{t('FindProfiles')}</h3>

      <FiltersCard isActive={true}>
        <Formik
          initialValues={{
            Username: '',
            FirstName: '',
            LastName: '',
            Fullname: '',
            Email: '',
            MobileNumber: '',
            ProfileId: '',
            Category: '',
            Exact: true,
            First: 0,
            Max: 100,
          } as IProfileFilterDto}
          onSubmit={async (values) => {
            await performSearch(values);
          }}
        >
          {({ handleSubmit, resetForm }) => (
            <Form onSubmit={handleSubmit}>
              <div className="mb-3 row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">{t('Username')}</label>
                  <Field name="Username" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('FirstName')}</label>
                  <Field name="FirstName" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('LastName')}</label>
                  <Field name="LastName" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('FullName')}</label>
                  <Field name="Fullname" className="form-control" />
                </div>

                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('Email')}</label>
                  <Field name="Email" className="form-control" />
                </div>
                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('MobileNumber')}</label>
                  <Field name="MobileNumber" className="form-control" />
                </div>
                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('ProfileId')}</label>
                  <Field name="ProfileId" className="form-control" />
                </div>
                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('Category')}</label>
                  <Field name="Category" as="select" className="input-wrapper form-control">
                    <option value="" disabled>{t('SelectOption')}</option>
                    {dropdownOptions.Category.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Field>
                </div>

                <div className="col-sm-12 col-md-6 col-lg-4 col-xl-3 mt-2 d-flex align-items-center ">
                  <div className="form-check me-3 mb-2">
                    <Field type="checkbox" name="Exact" className="form-check-input" id="exact" />
                    <label className="form-check-label" htmlFor="exact">
                      {t('Exact')}
                    </label>
                  </div>
                  <div className="d-flex mb-2">
                    <button type="submit" className="btn gg-btn gg-btn-submit me-2 ">
                      {t('Search')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary text-white "
                      onClick={() => {
                        resetForm();
                        handleReset();
                      }}
                    >
                      {t('Reset')}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </FiltersCard>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading && <Loader />}

      {!hasSearched && !loading && (
        <div className="alert alert-info">
          {t('SetFiltersAndSearch') ?? 'Please set filters above and click Search to load results.'}
        </div>
      )}

      {hasSearched  && (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-sm table-hover" >
              <thead>
                <tr >
                  <th style={{ cursor: 'pointer' }} onClick={() => {
                    if (sortField === 'username') setAsc(!asc); else { setSortField('username'); setAsc(true); }
                  }}>
                    {t('Username')} <SortDirection isActive={sortField === 'username'} order={asc ? 'asc' : 'desc'} />
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => {
                    if (sortField === 'fullname') setAsc(!asc); else { setSortField('fullname'); setAsc(true); }
                  }}>
                    {t('FullName')} <SortDirection isActive={sortField === 'fullname'} order={asc ? 'asc' : 'desc'} />
                  </th>
                  <th>{t('ProfileState')}</th>
                  <th>{t('Approved')}</th>
                  <th>{t('Email')}</th>
                  <th>{t('MobileNumber')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody className="table-group-divider">
                {results.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="text-center">
                      {t('NoDataFound') ?? 'No profiles'}
                    </td>
                  </tr>
                )}

                {(() => {
                  const sorted = results.slice().sort((a, b) => {
                    const getVal = (p: IProfileItemDto) => {
                      if (sortField === 'username') return (p.Username ?? '').toString();
                      return (p.Name ?? p.Fullname ?? '').toString();
                    };
                    const va = getVal(a).toLowerCase();
                    const vb = getVal(b).toLowerCase();
                    const cmp = va.localeCompare(vb);
                    return asc ? cmp : -cmp;
                  });

                  const startIndex = (page - 1) * pageSize;
                  const paged = sorted.slice(startIndex, startIndex + pageSize);
                  return paged.map((p, idx) => (
                    <tr key={p.ProfileId ?? `idx-${idx}`} 
                        className="clickable-row text-primary"
                        style={{ cursor: 'pointer' }}
                        data-bs-toggle="tooltip"
                        title={t('ViewProfileDetails')}
                        onClick={() => goToProfileDetails(p)}
                      >
                        <td>{p.Username}</td>
                        <td>{p.Name ?? p.Fullname ?? ''}</td>
                        <td>{p.ProfileState ?? ''}</td>
                        <td>{p.Approved ? 'Yes' : 'No'}</td>
                        <td>{p.Email}</td>
                        <td>{p.MobileNumber}</td>
                        <td onClick={(e) => e.stopPropagation()}> 
                          <button
                            className="btn gg-btn gg-btn-submit"
                            onClick={() => goToProfileEvents(p)}
                          >
                            {t('ViewEvents')}
                          </button>
                        </td>
                      </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
          <div>
            <Pagination
              currentPage={page}
              onPageChange={(p) => setPage(p)}
              totalItems={results.length}
              pageSize={pageSize}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(1);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};
